// Galeria do kit (`spec/tasks/WEB-50.md` item 3): o `Preview` de cada componente de
// `components/kit/*` com os MESMOS dados do croqui, para o screenshot lado a lado.
//
// Só em dev: `beforeLoad` derruba a rota em qualquer build que não seja `import.meta.env.DEV`,
// então a galeria nunca aparece em staging nem em produção.

import { createFileRoute, notFound } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';
import { toast } from 'sonner';
import { sellerNav } from '../app/nav-seller.ts';
import { CodeBlock } from '../components/app/code-block.tsx';
import { ConfirmDialog } from '../components/app/confirm-dialog.tsx';
import { IconListCard, type IconListItem } from '../components/app/icon-list-card.tsx';
import { KeyValue, type KeyValueRow } from '../components/app/key-value.tsx';
import { ListRow } from '../components/app/list-row.tsx';
import { ListToolbar as ZenithListToolbar } from '../components/app/list-toolbar.tsx';
import { Mascot, Wordmark } from '../components/app/mascot.tsx';
import { Pager as ZenithPager } from '../components/app/pager.tsx';
import { RouteDrawer } from '../components/app/route-drawer.tsx';
import { ThemeToggle } from '../components/app/theme-toggle.tsx';
import { type TimelineItem, Timeline as ZenithTimeline } from '../components/app/timeline.tsx';
import { ToggleRow } from '../components/app/toggle-row.tsx';
import { TxStatusBadge } from '../components/app/tx-status-badge.tsx';
import { VolumeCard, type VolumePoint } from '../components/app/volume-card.tsx';
import {
  AlertTriangle,
  Copy,
  MoreHorizontal,
  Plus,
  Search,
  ShieldCheck,
  Trash2,
  Webhook,
} from '../components/icons/index.tsx';
import { Amount } from '../components/kit/amount.tsx';
import { Badge } from '../components/kit/badge.tsx';
import { Bars } from '../components/kit/bars.tsx';
import { Btn, IconBtn } from '../components/kit/btn.tsx';
import { CalendarMonth } from '../components/kit/calendar-month.tsx';
import { Card } from '../components/kit/card.tsx';
import { DateRangeFilter, rangeForPreset } from '../components/kit/date-range-filter.tsx';
import { DetailRow } from '../components/kit/detail-row.tsx';
import { Divider } from '../components/kit/divider.tsx';
import { Drawer } from '../components/kit/drawer.tsx';
import { Empty } from '../components/kit/empty.tsx';
import { Eyebrow } from '../components/kit/eyebrow.tsx';
import { Field } from '../components/kit/field.tsx';
import { Footnote } from '../components/kit/footnote.tsx';
import { ICON_NAMES, Icon } from '../components/kit/icon.tsx';
import { IconItem } from '../components/kit/icon-item.tsx';
import type { Ink } from '../components/kit/ink.ts';
import { Input } from '../components/kit/input.tsx';
import { Kbd } from '../components/kit/kbd.tsx';
import { KpiGrid } from '../components/kit/kpi-grid.tsx';
import { KV, KVGroup } from '../components/kit/kv.tsx';
import { ListToolbar } from '../components/kit/list-toolbar.tsx';
import { brl, brlSigned, pct, pctOf } from '../components/kit/money.ts';
import { Mono } from '../components/kit/mono.tsx';
import { Notice } from '../components/kit/notice.tsx';
import { Num } from '../components/kit/num.tsx';
import { PageHeader } from '../components/kit/page-header.tsx';
import { Pager } from '../components/kit/pager.tsx';
import { Panel } from '../components/kit/panel.tsx';
import { Pill } from '../components/kit/pill.tsx';
import { ProfileCard } from '../components/kit/profile-card.tsx';
import { Progress } from '../components/kit/progress.tsx';
import { Rail } from '../components/kit/rail.tsx';
import { RevenueCard } from '../components/kit/revenue-card.tsx';
import { SearchInput } from '../components/kit/search-input.tsx';
import { Segmented } from '../components/kit/segmented.tsx';
import { Select } from '../components/kit/select.tsx';
import { Sidebar } from '../components/kit/sidebar.tsx';
import { Stat } from '../components/kit/stat.tsx';
import { type Col, Table } from '../components/kit/table.tsx';
import { IconTile, Tile } from '../components/kit/tile.tsx';
import { Timeline } from '../components/kit/timeline.tsx';
import { Toggle } from '../components/kit/toggle.tsx';
import { TwoLine } from '../components/kit/two-line.tsx';
import { TRANSACTION_STATUSES, TxStatusPill } from '../components/kit/tx-status.tsx';
import { Button } from '../components/ui/button.tsx';
import { Checkbox } from '../components/ui/checkbox.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu.tsx';
import {
  FieldDescription,
  FieldError,
  FieldLabel,
  Field as UiField,
} from '../components/ui/field.tsx';
import { InputAddon, InputGroup, Input as UiInput } from '../components/ui/input.tsx';
import { Label } from '../components/ui/label.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover.tsx';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group.tsx';
import { Segmented as UiSegmented } from '../components/ui/segmented.tsx';
import {
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Select as UiSelect,
} from '../components/ui/select.tsx';
import { Switch } from '../components/ui/switch.tsx';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs.tsx';
import { Textarea } from '../components/ui/textarea.tsx';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip.tsx';

const INKS: Ink[] = ['text', 'muted', 'faint', 'gold', 'green', 'danger'];

// `data-surface`: a seção da galeria é um cartão de verdade, como o `Panel` das telas. O
// `Card` do kit aqui dentro desenha como célula interna, que é o uso real dele.
function Section({ name, children }: { name: string; children: ReactNode }) {
  return (
    <section data-kit={name} className="flex flex-col gap-3">
      <Eyebrow>{name}</Eyebrow>
      {/* Os exemplos têm largura fixa do croqui (`w-100`, `w-140`...): o `max-w-full` os
          encolhe no celular em vez de empurrarem a página. */}
      <div
        data-surface=""
        className="rounded-2xl bg-card p-5 shadow-(--shadow-card) [&_[class*='w-']]:max-w-full"
      >
        {children}
      </div>
    </section>
  );
}

function Sub({ children }: { children: ReactNode }) {
  return <p className="mb-2 text-12 text-muted">{children}</p>;
}

/**
 * Primitivos de `components/ui` em todos os estados (WEB-133): é daqui que saem as fotos de
 * botão, campo, aba e flutuante. Estado próprio pra não inflar o `KitGallery`.
 */
function SystemGallery() {
  const [period, setPeriod] = useState<'today' | '7d' | '30d'>('30d');
  const [confirm, setConfirm] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [onSale, setOnSale] = useState(true);

  return (
    <>
      <Section name="Botão (ui/button)">
        <div className="flex flex-col gap-5">
          <div>
            <Sub>Variantes</Sub>
            <div className="flex flex-wrap items-center gap-2">
              <Button>Nova cobrança</Button>
              <Button variant="outline">Exportar CSV</Button>
              <Button variant="secondary">Secundário</Button>
              <Button variant="ghost">Cancelar</Button>
              <Button variant="destructive">Desativar 2FA</Button>
              <Button variant="destructive-solid">Remover chave</Button>
              <Button variant="link">Ver documentação</Button>
            </div>
          </div>
          <div>
            <Sub>Tamanhos, ícone e estados</Sub>
            <div className="flex flex-wrap items-center gap-2">
              <Button size="xs">xs</Button>
              <Button size="sm">sm</Button>
              <Button>default</Button>
              <Button size="lg">lg</Button>
              <Button>
                <Plus />
                Criar produto
              </Button>
              <Button variant="outline" size="icon" aria-label="Copiar">
                <Copy />
              </Button>
              <Button variant="ghost" size="icon-sm" aria-label="Mais ações">
                <MoreHorizontal />
              </Button>
              <Button disabled>Desabilitado</Button>
              <Button variant="outline" disabled>
                Desabilitado
              </Button>
              <Button loading>Enviando…</Button>
              <Button variant="outline" loading>
                Gerando…
              </Button>
            </div>
          </div>
        </div>
      </Section>

      <Section name="Campos (ui/input, textarea, select, checkbox, radio, switch)">
        <div className="flex flex-col gap-6">
          <div className="grid gap-x-5 gap-y-5 sm:grid-cols-2 lg:grid-cols-3">
            <UiField>
              <FieldLabel htmlFor="g-nome">Nome do produto</FieldLabel>
              <UiInput id="g-nome" placeholder="Ex.: Curso de cerâmica" />
              <FieldDescription>É o que o comprador vê na página de pagamento.</FieldDescription>
            </UiField>
            <UiField>
              <FieldLabel htmlFor="g-email">E-mail</FieldLabel>
              <UiInput
                id="g-email"
                type="email"
                aria-invalid="true"
                aria-describedby="g-email-erro"
                defaultValue="marina@"
              />
              <FieldError id="g-email-erro">Digite um e-mail válido.</FieldError>
            </UiField>
            <UiField>
              <FieldLabel htmlFor="g-valor">Valor</FieldLabel>
              <InputGroup>
                <InputAddon>R$</InputAddon>
                <UiInput
                  id="g-valor"
                  inputMode="decimal"
                  className="data-value"
                  defaultValue="129,90"
                />
              </InputGroup>
              <FieldDescription>Em reais, com vírgula nos centavos.</FieldDescription>
            </UiField>
            <UiField>
              <FieldLabel htmlFor="g-busca">Busca</FieldLabel>
              <InputGroup>
                <InputAddon>
                  <Search />
                </InputAddon>
                <UiInput
                  id="g-busca"
                  type="search"
                  placeholder="Buscar por id, e-mail ou documento"
                />
              </InputGroup>
            </UiField>
            <UiField>
              <FieldLabel htmlFor="g-chave">Chave de API (somente leitura)</FieldLabel>
              <InputGroup className="pr-1">
                <UiInput
                  id="g-chave"
                  readOnly
                  className="font-mono"
                  defaultValue="sk_live_9f2c41b7"
                />
                <Button variant="ghost" size="icon-sm" aria-label="Copiar chave">
                  <Copy />
                </Button>
              </InputGroup>
            </UiField>
            <UiField>
              <FieldLabel htmlFor="g-off">Desabilitado</FieldLabel>
              <UiInput id="g-off" disabled defaultValue="Loja Aurora Ltda" />
            </UiField>
            <UiField>
              <FieldLabel htmlFor="g-ro">Somente leitura</FieldLabel>
              <UiInput id="g-ro" readOnly defaultValue="12.345.678/0001-90" />
            </UiField>
            <UiField>
              <FieldLabel htmlFor="g-desc">Descrição</FieldLabel>
              <Textarea id="g-desc" placeholder="Texto puro, até 500 caracteres." />
            </UiField>
            <UiField>
              <FieldLabel htmlFor="g-desc-erro">Motivo</FieldLabel>
              <Textarea id="g-desc-erro" aria-invalid="true" aria-describedby="g-motivo-erro" />
              <FieldError id="g-motivo-erro">
                Explique o motivo em pelo menos 10 caracteres.
              </FieldError>
            </UiField>
          </div>

          <div>
            <Sub>Select do Radix e select nativo do kit: o mesmo controle</Sub>
            <div className="flex flex-wrap items-center gap-2.5">
              <UiSelect defaultValue="created">
                <SelectTrigger aria-label="Ordenação (Radix)" className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created">Criada em</SelectItem>
                  <SelectItem value="paid">Paga em</SelectItem>
                </SelectContent>
              </UiSelect>
              <Select aria-label="Ordenação (nativo)" className="w-44" defaultValue="created">
                <option value="created">Criada em</option>
                <option value="paid">Paga em</option>
              </Select>
              <Select aria-label="Desabilitado" className="w-44" disabled defaultValue="created">
                <option value="created">Desabilitado</option>
              </Select>
            </div>
          </div>

          <div>
            <Sub>Linha de filtro: campo, selects, segmentado e botões na mesma altura</Sub>
            <div className="flex flex-wrap items-center gap-2.5">
              <InputGroup className="w-72">
                <InputAddon>
                  <Search />
                </InputAddon>
                <UiInput
                  type="search"
                  aria-label="Buscar transação"
                  placeholder="Buscar por id ou e-mail"
                />
              </InputGroup>
              <UiSegmented<'today' | '7d' | '30d'>
                aria-label="Período"
                value={period}
                onChange={setPeriod}
                options={[
                  ['today', 'Hoje'],
                  ['7d', '7 dias'],
                  ['30d', '30 dias'],
                ]}
              />
              <UiSelect defaultValue="created">
                <SelectTrigger aria-label="Data usada no filtro" className="w-36">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created">Criada em</SelectItem>
                  <SelectItem value="paid">Paga em</SelectItem>
                </SelectContent>
              </UiSelect>
              <Select aria-label="Status" defaultValue="all">
                <option value="all">Todos os status</option>
                <option value="paid">Pagas</option>
              </Select>
              <Button variant="outline">Exportar CSV</Button>
              <Button>Nova cobrança</Button>
            </div>
          </div>

          <div className="flex flex-wrap items-start gap-x-10 gap-y-5">
            <div className="flex flex-col gap-3">
              <Sub>Checkbox</Sub>
              <div className="flex items-center gap-2">
                <Checkbox id="g-c1" defaultChecked />
                <Label htmlFor="g-c1">Enviar recibo por e-mail</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="g-c2" />
                <Label htmlFor="g-c2">Cobrar taxa do comprador</Label>
              </div>
              <div className="flex items-center gap-2">
                <Checkbox id="g-c3" disabled />
                <Label htmlFor="g-c3">Desabilitado</Label>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Sub>Radio</Sub>
              <RadioGroup defaultValue="pix" aria-label="Chave de destino">
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="g-r1" value="pix" />
                  <Label htmlFor="g-r1">Chave Pix principal</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id="g-r2" value="outra" />
                  <Label htmlFor="g-r2">Outra chave</Label>
                </div>
              </RadioGroup>
            </div>
            <div className="flex flex-col gap-3">
              <Sub>Switch</Sub>
              <div className="flex items-center gap-2">
                <Switch id="g-s1" checked={onSale} onCheckedChange={setOnSale} />
                <Label htmlFor="g-s1">À venda</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="g-s2" />
                <Label htmlFor="g-s2">Saque automático</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch id="g-s3" disabled defaultChecked />
                <Label htmlFor="g-s3">Desabilitado</Label>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section name="Abas (ui/tabs) e segmentado (ui/segmented)">
        <div className="flex flex-wrap items-start gap-x-10 gap-y-6">
          <Tabs defaultValue="product" className="w-80">
            <TabsList className="w-full">
              <TabsTrigger value="product">Produto</TabsTrigger>
              <TabsTrigger value="delivery">Entrega</TabsTrigger>
              <TabsTrigger value="off" disabled>
                Limites
              </TabsTrigger>
            </TabsList>
            <TabsContent value="product" className="text-13 text-muted">
              Painel da aba Produto.
            </TabsContent>
            <TabsContent value="delivery" className="text-13 text-muted">
              Painel da aba Entrega.
            </TabsContent>
          </Tabs>
          <Tabs defaultValue="keys" className="w-80">
            <TabsList variant="line" className="w-full justify-start">
              <TabsTrigger value="keys" className="flex-none">
                Chaves de API
              </TabsTrigger>
              <TabsTrigger value="hooks" className="flex-none">
                Webhooks
              </TabsTrigger>
            </TabsList>
            <TabsContent value="keys" className="text-13 text-muted">
              Variante em linha: traço de ativo em ouro.
            </TabsContent>
            <TabsContent value="hooks" className="text-13 text-muted">
              Painel de Webhooks.
            </TabsContent>
          </Tabs>
        </div>
      </Section>

      <Section name="Flutuantes (dialog, sheet, popover, dropdown, tooltip, toast)">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={() => {
              setConfirm(true);
            }}
          >
            Abrir diálogo
          </Button>
          <Button
            variant="outline"
            onClick={() => {
              setFormOpen(true);
            }}
          >
            Abrir gaveta de formulário
          </Button>
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline">Abrir popover</Button>
            </PopoverTrigger>
            <PopoverContent align="start" aria-label="Reserva">
              <p className="text-14 font-medium">Reserva</p>
              <p className="mt-1 text-13 text-muted">10% de cada venda fica retido por 30 dias.</p>
            </PopoverContent>
          </Popover>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">Abrir menu</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-48">
              <DropdownMenuLabel>Chave</DropdownMenuLabel>
              <DropdownMenuItem>
                <Copy />
                Copiar id
              </DropdownMenuItem>
              <DropdownMenuItem>Renomear</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive">
                <Trash2 />
                Revogar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button variant="outline">Tooltip</Button>
              </TooltipTrigger>
              <TooltipContent>Líquido = valor menos taxa e reserva</TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <Button
            variant="outline"
            onClick={() => {
              toast.success('Chave copiada', { duration: 60_000 });
              toast.error('Não deu para salvar', {
                description: 'A API respondeu 503. Tente de novo em instantes.',
                duration: 60_000,
              });
              toast.warning('Saque em análise', { duration: 60_000 });
              toast('Exportação pronta', { duration: 60_000 });
            }}
          >
            Disparar toasts
          </Button>
        </div>

        <ConfirmDialog
          open={confirm}
          destructive
          title="Remover a chave Pix?"
          description="Os saques programados para esta chave são cancelados. Não dá para desfazer."
          confirmLabel="Remover chave"
          onConfirm={() => {
            setConfirm(false);
          }}
          onOpenChange={setConfirm}
        />

        <RouteDrawer
          open={formOpen}
          onClose={() => {
            setFormOpen(false);
          }}
          title="Criar produto"
          sub="O que você vende, com o preço."
          footer={
            <>
              <Button
                variant="ghost"
                onClick={() => {
                  setFormOpen(false);
                }}
              >
                Cancelar
              </Button>
              <Button>Criar e continuar</Button>
            </>
          }
        >
          <UiField>
            <FieldLabel htmlFor="gd-nome">Nome</FieldLabel>
            <UiInput id="gd-nome" placeholder="Ex.: Curso de cerâmica" />
            <FieldDescription>É o que o comprador vê na página de pagamento.</FieldDescription>
          </UiField>
          <UiField>
            <FieldLabel htmlFor="gd-desc">Descrição</FieldLabel>
            <Textarea id="gd-desc" placeholder="Texto puro, até 500 caracteres." />
          </UiField>
          <div className="grid gap-4 border-t pt-4">
            <span className="micro-label">Preço</span>
            <UiField>
              <FieldLabel htmlFor="gd-valor">Valor</FieldLabel>
              <InputGroup>
                <InputAddon>R$</InputAddon>
                <UiInput
                  id="gd-valor"
                  inputMode="decimal"
                  className="data-value"
                  aria-invalid="true"
                  aria-describedby="gd-valor-erro"
                  defaultValue="0,00"
                />
              </InputGroup>
              <FieldError id="gd-valor-erro">O valor mínimo é R$ 1,00.</FieldError>
            </UiField>
          </div>
          <div className="grid gap-4 border-t pt-4">
            <span className="micro-label">Venda</span>
            <ToggleRow
              title="À venda"
              sub="Desligado, os links desse produto param de cobrar."
              checked={onSale}
              onChange={setOnSale}
              last
              className="py-0"
            />
          </div>
          <UiField>
            <FieldLabel htmlFor="gd-obs">Observações internas</FieldLabel>
            <Textarea id="gd-obs" className="min-h-40" placeholder="Só a sua equipe vê." />
          </UiField>
        </RouteDrawer>
      </Section>
    </>
  );
}

interface TxRow {
  id: string;
  payer: string;
  cents: number;
  status: string;
  at: string;
}

const TX_ROWS: TxRow[] = [
  { id: 'tx_01J9K2', payer: 'Carlos M.', cents: 12_990, status: 'paid', at: '14:32' },
  { id: 'tx_01J9K1', payer: 'Juliana S.', cents: 34_900, status: 'paid', at: '14:19' },
  { id: 'tx_01J9JZ', payer: 'Pedro A.', cents: 8990, status: 'chargeback', at: '13:58' },
];

const TX_COLS: Col<TxRow>[] = [
  {
    key: 'at',
    label: 'Hora',
    w: '70px',
    compact: true,
    render: (r) => <Mono ink="muted">{r.at}</Mono>,
  },
  { key: 'payer', label: 'Pagador', render: (r) => r.payer },
  {
    key: 'amount',
    label: 'Valor',
    w: '120px',
    align: 'right',
    compact: true,
    render: (r) => <Amount cents={r.cents} ink="text" />,
  },
  {
    key: 'status',
    label: 'Status',
    w: '130px',
    align: 'right',
    compact: true,
    render: (r) => <TxStatusPill status={r.status} />,
  },
];

const ZENITH_VOLUME: VolumePoint[] = [
  { date: '2026-09-11', cents: 390_000 },
  { date: '2026-09-12', cents: 720_000 },
  { date: '2026-09-13', cents: 680_000 },
  { date: '2026-09-14', cents: 810_000 },
  { date: '2026-09-15', cents: 522_500 },
];

const ZENITH_ICON_ITEMS: IconListItem[] = [
  {
    key: 'kyc',
    icon: ShieldCheck,
    tone: 'warning',
    title: 'Verificação: envie o contrato social',
    sub: 'kyc_status pending · saque bloqueado',
  },
  {
    key: 'webhooks',
    icon: Webhook,
    tone: 'danger',
    title: '3 entregas de webhook falharam',
    sub: 'HTTP 502 · 3ª tentativa em 30 min',
    onClick: () => undefined,
  },
];

const ZENITH_KV_ROWS: KeyValueRow[] = [
  { key: 'status', label: 'Status', value: <TxStatusBadge status="paid" /> },
  { key: 'mode', label: 'Modo', value: 'live' },
  { key: 'net', label: 'Líquido', value: 'R$ 127,56' },
];

const ZENITH_TIMELINE: TimelineItem[] = [
  { at: '14:19', label: 'Cobrança criada', detail: 'R$ 129,90 · QR dinâmico', tone: 'wait' },
  { at: '14:32', label: 'Pix pago', detail: 'E2E a3f9c1 · Carlos M.', tone: 'ok' },
  { at: '14:33', label: 'Webhook entregue', detail: 'transaction.paid · HTTP 200', tone: 'ok' },
];

function KitGallery() {
  const [seg, setSeg] = useState('paid');
  const [toggle, setToggle] = useState(true);
  const [range, setRange] = useState(rangeForPreset('30d'));
  const [calendar, setCalendar] = useState({ from: range.from, to: range.to });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [zenithToggle, setZenithToggle] = useState(true);
  const [zenithDrawerOpen, setZenithDrawerOpen] = useState(false);

  return (
    <div className="flex min-h-dvh flex-col gap-8 bg-bg p-8">
      <div className="flex items-start justify-between gap-4">
        <PageHeader
          title="Kit do croqui"
          sub="Preview de cada componente de components/kit"
          path="/dev/kit"
          size="lg"
        />
        <ThemeToggle />
      </div>

      <SystemGallery />

      <Section name="Amount">
        <div className="flex flex-wrap items-start gap-3">
          <Amount cents={12_990} />
          <Amount cents={-234} ink="muted" />
          <Amount cents={0} ink="faint" />
          <Amount cents={2_485_000} ink="gold" />
          <Amount cents={11_480} ink="green" sign="ledger" />
          <Amount cents={-8990} ink="danger" />
          <Amount cents={5_732_500} size={18} weight={700} ink="gold" />
        </div>
      </Section>

      <Section name="Badge">
        <div className="flex flex-wrap items-start gap-3">
          {INKS.map((k) => (
            <Badge key={k} ink={k}>
              {k === 'text' ? '3' : k}
            </Badge>
          ))}
          <Badge>transaction.reserve_released</Badge>
        </div>
      </Section>

      <Section name="Bars">
        <div className="flex w-[480px] flex-col gap-5">
          <Bars
            data={[
              { label: '07', value: 3900 },
              { label: '08', value: 7200 },
              { label: '09', value: 6800 },
              { label: '10', value: 8100 },
              { label: '11', value: 5225 },
            ]}
            highlight={4}
          />
          <Bars
            height={34}
            gap={3}
            labels={false}
            highlight={2}
            data={[
              { label: '<50', value: 64 },
              { label: '50 a 100', value: 131 },
              { label: '100 a 200', value: 152 },
              { label: '>500', value: 22 },
            ]}
          />
        </div>
      </Section>

      <Section name="Btn / IconBtn">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap gap-3">
            <Btn kind="primary" icon="arrow">
              Sacar
            </Btn>
            <Btn kind="card" icon="doc">
              Exportar
            </Btn>
            <Btn kind="ghost" icon="swap">
              Ver todas
            </Btn>
            <Btn kind="danger" icon="alert">
              Recusar
            </Btn>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Btn kind="primary" small icon="arrow">
              Sacar
            </Btn>
            <Btn kind="card" small>
              Ver transação
            </Btn>
            <Btn kind="card" disabled>
              Indisponível
            </Btn>
            <IconBtn icon="doc" label="Copiar id" />
            <IconBtn icon="alert" label="Falhou" ink="danger" />
            <IconBtn icon="copy" label="Copiar chave" ink="text" />
          </div>
        </div>
      </Section>

      <Section name="CalendarMonth">
        <CalendarMonth value={calendar} onSelect={setCalendar} months={2} />
      </Section>

      <Section name="Card">
        <div className="flex flex-wrap items-start gap-3">
          <Card className="w-50">
            <div className="text-13 font-semibold">Disponível</div>
            <div className="mt-1 text-12 text-muted">R$ 24.850,00</div>
          </Card>
          <Card active className="w-50">
            <div className="text-13 font-semibold">Linha selecionada</div>
            <div className="mt-1 text-12 text-muted">tx_01J9K2 · Pix pago</div>
          </Card>
        </div>
      </Section>

      <Section name="DateRangeFilter">
        <DateRangeFilter value={range} onChange={setRange} />
      </Section>

      <Section name="DetailRow">
        <div className="w-100">
          <DetailRow
            title="Pix recebido · Carlos M."
            sub="E2E a3f9… · 10/09 14:32"
            right={<Amount cents={12_990} ink="text" />}
          />
          <DetailRow
            title="Taxa"
            sub="account: platform_fees"
            right={<Amount cents={-234} ink="muted" />}
          />
          <DetailRow
            title="Estorno · MED 0021"
            sub="prazo 14/09 18h"
            right={<Amount cents={-8990} ink="danger" />}
          />
          <DetailRow
            last
            title={<span className="font-semibold">Disponível</span>}
            right={<Amount cents={2_485_000} size={18} weight={700} ink="gold" />}
          />
        </div>
      </Section>

      <Section name="Divider">
        <div className="flex w-90 flex-col gap-4">
          <span className="text-12 text-muted">Disponível</span>
          <Divider />
          <span className="text-12 text-muted">Reserva</span>
          <div className="flex h-12 items-stretch gap-3">
            <span className="text-12 text-muted">Pix</span>
            <Divider vertical />
            <span className="text-12 text-muted">Saque</span>
          </div>
        </div>
      </Section>

      <Section name="Drawer">
        <Btn
          kind="card"
          onClick={() => {
            setDrawerOpen(true);
          }}
        >
          Abrir drawer
        </Btn>
        <Drawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          title="Pix recebido"
          sub="led_01J9K2R3 · 11/09 14:32"
        >
          <KV k="account" v="available" monoV />
          <KV k="amount_cents" v={<Amount cents={11_480} ink="green" sign="ledger" />} />
          <KV k="memo" v="Carlos Mendes · taxa R$ 2,34" last />
        </Drawer>
      </Section>

      <Section name="Empty">
        <div className="flex flex-wrap items-start gap-3">
          <Empty title="Nenhum Pix hoje" className="flex-1" />
          <Empty
            className="flex-1"
            icon="swap"
            title="Nenhuma transação"
            hint="Crie uma cobrança ou aguarde o primeiro pagamento."
            action={
              <Btn kind="primary" small>
                Nova cobrança
              </Btn>
            }
          />
        </div>
      </Section>

      <Section name="Eyebrow">
        <div className="flex flex-wrap items-start gap-4">
          <Eyebrow>Geral</Eyebrow>
          <Eyebrow small>Líquido</Eyebrow>
          <Eyebrow className="text-gold">Faturamento</Eyebrow>
        </div>
      </Section>

      <Section name="Field / Input / Select">
        <div className="flex flex-wrap items-start gap-4">
          <Field label="Chave Pix" hint="CNPJ da loja, conferido no KYC" className="w-70">
            {(id) => (
              <Input id={id} defaultValue="12.345.678/0001-90" right={<Badge>CNPJ</Badge>} />
            )}
          </Field>
          <Field label="Webhook URL" className="w-70">
            {(id) => <Input id={id} placeholder="https://api.lojadorafa.com.br/hooks" />}
          </Field>
          <Field label="Status" className="w-45">
            {(id) => (
              <Select id={id} defaultValue="paid">
                {TRANSACTION_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </Section>

      <Section name="Footnote">
        <div className="flex w-105 flex-col gap-2">
          <Footnote left="pagos · expirados" right="meta 90 %" />
          <Footnote left="<50" mid="faixa mais comum 100 a 200" right=">500" />
          <Footnote left="Σ débitos = Σ créditos" check="balanceado" />
        </div>
      </Section>

      <Section name="Icon">
        <div className="flex flex-wrap items-start gap-3">
          {ICON_NAMES.map((name) => (
            <div key={name} className="flex w-18 flex-col items-center gap-1.5">
              <Icon name={name} size={20} />
              <Mono size={10}>{name}</Mono>
            </div>
          ))}
        </div>
      </Section>

      <Section name="IconItem">
        <div className="flex w-105 flex-col gap-3">
          <IconItem
            icon="shield"
            ink="gold"
            title="Verificação: envie o contrato social"
            sub="kyc_status pending · saque bloqueado"
          />
          <IconItem
            icon="webhook"
            ink="danger"
            title="3 entregas de webhook falharam"
            sub="HTTP 502 · 3ª tentativa em 30 min"
            last
          />
          <IconItem
            tile={false}
            icon="lock"
            ink="gold"
            title="A taxa é congelada na venda."
            sub="Mudança de tabela não altera cobranças já pagas."
          />
        </div>
      </Section>

      <Section name="KV / KVGroup">
        <div className="flex flex-wrap items-start gap-6">
          <div className="w-70">
            <KV k="Webhooks entregues" v={<span className="text-green">98,7 %</span>} />
            <KV k="Latência média API" v="142 ms" />
            <KV k="mode" v="live" monoV last />
          </div>
          <KVGroup
            className="w-70"
            title="Referência"
            rows={[
              { k: 'reference.type', v: 'transaction', monoV: true },
              { k: 'reference.id', v: 'tx_01J9K2QH7V', monoV: true },
              { k: 'memo', v: 'Carlos Mendes · taxa R$ 2,34' },
            ]}
          />
        </div>
      </Section>

      <Section name="Kbd">
        <div className="flex flex-wrap items-start gap-3">
          <Kbd>⌘K</Kbd>
          <Kbd>Enter</Kbd>
          <Kbd>sk_live_</Kbd>
          <Kbd>verify-balances</Kbd>
        </div>
      </Section>

      <Section name="KpiGrid">
        <div className="flex w-130 flex-col gap-5">
          <KpiGrid
            items={[
              { k: 'Líquido', cents: 5_639_660 },
              { k: 'Taxas', cents: -92_840 },
              { k: 'Hoje', cents: 522_510 },
            ]}
          />
          <KpiGrid
            size={11}
            items={[
              { k: 'Bruto', cents: 5_732_500 },
              { k: 'Taxas', cents: -92_840 },
              { k: 'Estornos', cents: -131_240 },
              { k: 'Sacado', cents: -3_012_930 },
            ]}
          />
        </div>
      </Section>

      <Section name="ListToolbar">
        <ListToolbar
          tabs={[
            { id: 'all', label: 'Todas', count: 489 },
            { id: 'paid', label: 'Pagas', count: 447 },
            { id: 'pending', label: 'Aguardando', count: 3 },
          ]}
          value={seg}
          onChange={setSeg}
          tabsLabel="Filtro da lista"
          extra={
            <Btn kind="card" small icon="doc">
              CSV
            </Btn>
          }
          right={
            <>
              489 no período · <Amount cents={5_732_500} size={12} /> pagos
            </>
          }
        />
      </Section>

      <Section name="Mascot / Wordmark">
        <div className="flex flex-wrap items-end gap-4">
          {[64, 44, 32, 24].map((size) => (
            <Mascot key={size} size={size} />
          ))}
          <div className="text-19 font-bold tracking-[-0.01em]">
            <Wordmark />
          </div>
        </div>
      </Section>

      <Section name="money">
        <div className="flex w-130 flex-col gap-2 font-mono text-13">
          {[
            ['brl(12990)', brl(12_990)],
            ['brlSigned(12756)', brlSigned(12_756)],
            ['brlSigned(-1500)', brlSigned(-1500)],
            ['pct(149)', pct(149)],
            ['pctOf(10000, 149)', String(pctOf(10_000, 149))],
            ['brl(pctOf(10000, 149))', brl(pctOf(10_000, 149))],
          ].map(([expr, out]) => (
            <div key={expr} className="flex justify-between gap-4">
              <span className="text-muted">{expr}</span>
              <span className="tabular-nums">{out}</span>
            </div>
          ))}
        </div>
      </Section>

      <Section name="Mono">
        <div className="flex flex-col gap-2.5">
          <Mono>tx_01J9K2QH7V</Mono>
          <Mono size={11} ink="text">
            /app/transactions
          </Mono>
          <Mono size={12} ink="gold">
            E2Ea3f9c1d2e7b04c9d81f0a6
          </Mono>
        </div>
      </Section>

      <Section name="Notice">
        <div className="flex flex-col gap-3">
          <Notice tone="ok" title="Saque de R$ 2.400,00 caiu na chave Pix.">
            Líquido após taxa de saque R$ 3,50.
          </Notice>
          <Notice
            tone="wait"
            icon="bolt"
            title="Mudança agendada para 01/10/2026 00:00."
            action={
              <Btn kind="card" small>
                Ver detalhes
              </Btn>
            }
          >
            Pix 1,49 % → 1,29 %. Vendas pagas antes disso mantêm a taxa de hoje.
          </Notice>
          <Notice tone="bad" title="Chargeback em tx_01J9JZ8R2P.">
            Débito de R$ 89,90 + taxa R$ 15,00 no extrato.
          </Notice>
          <Notice tone="neutral" icon="lock" title="Só vale para vendas pagas a partir da data." />
          <Notice tone="info" title="Sua tabela é negociada.">
            versão 3 · desde 01/08/2026
          </Notice>
        </div>
      </Section>

      <Section name="Num">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-end gap-4">
            {[18, 24, 32].map((size) => (
              <Num key={size} size={size}>
                R$ 129,90
              </Num>
            ))}
            <Num>R$ 57.325,00</Num>
          </div>
          <div className="flex flex-wrap items-end gap-4">
            {INKS.map((k) => (
              <Num key={k} size={24} ink={k}>
                R$ 4.210,00
              </Num>
            ))}
          </div>
        </div>
      </Section>

      <Section name="PageHeader">
        <div className="flex flex-col gap-6">
          <PageHeader
            title="Transações"
            sub="Todas as cobranças da conta"
            path="/v1/me/transactions"
            actions={
              <Btn kind="card" small>
                Exportar
              </Btn>
            }
          />
          <PageHeader title="Visão geral" sub="Últimos 30 dias" size="lg" />
        </div>
      </Section>

      <Section name="Pager">
        <div className="flex w-140 flex-col">
          <Pager from={1} to={12} total={489} onNext={() => undefined} />
          <Pager from={1} to={3} total={3} />
        </div>
      </Section>

      <Section name="Panel">
        <div className="flex flex-col gap-4">
          <Panel>
            <div className="text-13 text-muted">Pix recebidos no período.</div>
          </Panel>
          <Panel
            title="Simule uma venda"
            sub="a mesma conta quando o Pix é confirmado"
            right={<Badge ink="gold">centavo</Badge>}
          >
            <div className="text-13">Cliente paga R$ 100,00 · líquido R$ 98,11</div>
          </Panel>
          <Panel n="01" title="Marca">
            <div className="text-13 text-muted">
              <Wordmark />
            </div>
          </Panel>
        </div>
      </Section>

      <Section name="Pill / TxStatus">
        <div className="flex flex-wrap items-start gap-2">
          <Pill tone="ok">Pago</Pill>
          <Pill tone="wait">Aguardando</Pill>
          <Pill tone="bad">Chargeback</Pill>
          <Pill tone="neutral">Expirado</Pill>
          <Pill tone="info">Sua tabela</Pill>
          {TRANSACTION_STATUSES.map((status) => (
            <TxStatusPill key={status} status={status} />
          ))}
        </div>
      </Section>

      <Section name="Progress">
        <div className="flex w-105 flex-col gap-3.5">
          <Progress pct={0} height={6} />
          <Progress pct={40} label="40% da meta" />
          <Progress pct={100} ink="green" label="concluído" />
        </div>
      </Section>

      <Section name="ProfileCard / RevenueCard">
        <div className="flex flex-wrap items-start gap-4">
          <div className="w-59">
            <ProfileCard
              name="Rafael Moreira"
              email="rafael@lojadorafa.com.br"
              onLogout={() => undefined}
            />
          </div>
          <div className="w-59">
            <RevenueCard value="R$ 50.850,00" />
          </div>
          <div className="w-59">
            <RevenueCard value="R$ 50.850,00" goal="R$ 100.000,00" fill={51} />
          </div>
        </div>
      </Section>

      <Section name="SearchInput">
        <div className="flex w-140 flex-col gap-3">
          <SearchInput label="Buscar" />
          <SearchInput
            label="Buscar transação"
            placeholder="Buscar por E2E, id, nome, e-mail ou documento"
          />
          <SearchInput label="Buscar compacto" small className="w-70" />
        </div>
      </Section>

      <Section name="Segmented">
        <div className="flex flex-col items-start gap-3">
          <Segmented
            label="Período"
            items={[
              { id: 'today', label: 'Hoje' },
              { id: '7d', label: '7 dias' },
              { id: '30d', label: '30 dias' },
            ]}
            value="30d"
          />
          <Segmented
            label="Filtro da lista"
            items={[
              { id: 'all', label: 'Todas', count: 489 },
              { id: 'paid', label: 'Pagas', count: 447 },
              { id: 'pending', label: 'Aguardando', count: 3 },
            ]}
            value={seg}
            onChange={setSeg}
          />
          <Segmented
            label="Modo da conta"
            items={[
              { id: 'live', label: 'Produção', dot: 'green' },
              { id: 'test', label: 'Teste', dot: 'muted' },
            ]}
            value="live"
          />
        </div>
      </Section>

      <Section name="Sidebar / Rail">
        <div className="flex h-[900px] items-stretch gap-6 overflow-hidden">
          <Sidebar
            groups={sellerNav}
            activePath="/app/transactions"
            revenue={<RevenueCard value="R$ 50.850,00" />}
            footer={
              <ProfileCard
                name="Rafael Moreira"
                email="rafael@lojadorafa.com.br"
                onLogout={() => undefined}
              />
            }
            badges={{ withdrawals: 3, webhooks: 7 }}
          />
          <Rail groups={sellerNav} activePath="/app/transactions" />
        </div>
      </Section>

      <Section name="Stat">
        <div className="flex flex-wrap items-stretch gap-3">
          <div className="w-65">
            <Stat label="Disponível" value="R$ 18.420,00" />
          </div>
          <div className="w-65">
            <Stat
              label="Reserva"
              icon="lock"
              code="reserved"
              value="R$ 4.210,00"
              ink="muted"
              sub="libera em 7 dias"
            />
          </div>
          <div className="w-90">
            <Stat
              label="Vendas no período"
              icon="swap"
              iconInk="gold"
              code="sales.gross_cents"
              value="R$ 57.325,00"
              size="lg"
              ink="gold"
              sub="447 pagas · setembro"
              foot={
                <KpiGrid
                  items={[
                    { k: 'Líquido', cents: 5_639_660 },
                    { k: 'Taxas', cents: -92_840 },
                  ]}
                />
              }
            />
          </div>
        </div>
      </Section>

      <Section name="Table">
        <div className="flex flex-col gap-6">
          <Table
            cols={TX_COLS}
            rows={TX_ROWS}
            rowKey={(r) => r.id}
            onRow={() => undefined}
            selected={(r) => r.id === 'tx_01J9K1'}
          />
          <Table
            cols={TX_COLS}
            rows={[]}
            rowKey={(r) => r.id}
            emptyTitle="Nenhum Pix neste período."
          />
        </div>
      </Section>

      <Section name="Tile / IconTile">
        <div className="flex flex-col gap-5">
          <div className="flex items-end gap-3">
            {[28, 32, 34, 40, 48].map((size) => (
              <Tile key={size} size={size} className="text-13 font-bold text-gold">
                {size}
              </Tile>
            ))}
          </div>
          <div className="flex flex-wrap gap-3">
            {INKS.map((k) => (
              <IconTile key={k} icon="ingot" ink={k} />
            ))}
            <IconTile icon="swap" size={28} iconSize={14} ink="green" />
            <IconTile icon="inbox" size={48} iconSize={22} />
          </div>
        </div>
      </Section>

      <Section name="Timeline">
        <div className="w-100">
          <Timeline
            items={[
              {
                at: '14:19',
                label: 'Cobrança criada',
                detail: 'R$ 129,90 · QR dinâmico',
                tone: 'info',
              },
              { at: '14:32', label: 'Pix pago', detail: 'E2E a3f9c1 · Carlos M.', tone: 'ok' },
              { at: '14:32', label: 'Taxa congelada', detail: '1,49 % + R$ 0,40 = R$ 2,34' },
              {
                at: '14:33',
                label: 'Webhook entregue',
                detail: 'transaction.paid · HTTP 200',
                tone: 'ok',
              },
            ]}
          />
        </div>
      </Section>

      <Section name="Toggle">
        <div className="flex flex-wrap items-start gap-4">
          <Toggle on={toggle} onChange={setToggle} label="Saque automático" />
          <Toggle on={false} label="Modo test" />
        </div>
      </Section>

      <Section name="TwoLine">
        <div className="flex gap-8 text-13">
          <TwoLine primary="14:32" secondary="11/09" mono />
          <TwoLine primary="Carlos Mendes" secondary="carlos.m@gmail.com" className="w-40" />
          <TwoLine primary="Sem secundária" />
        </div>
      </Section>

      <Section name="Zenith (components/app, WEB-101)">
        <div className="flex flex-col gap-6">
          <div>
            <p className="mb-2 text-12 text-muted">TxStatusBadge</p>
            <div className="flex flex-wrap gap-2">
              {[
                'paid',
                'pending',
                'failed',
                'refunded',
                'chargeback',
                'approved',
                'processing',
                'rejected',
                'awaiting',
                'successful',
                'cancelled',
                'algo_desconhecido',
              ].map((status) => (
                <TxStatusBadge key={status} status={status} />
              ))}
            </div>
          </div>

          <div>
            <p className="mb-2 text-12 text-muted">ListToolbar</p>
            <ZenithListToolbar right="489 no período">
              <TxStatusBadge status="paid" />
              <TxStatusBadge status="pending" />
            </ZenithListToolbar>
          </div>

          <div>
            <p className="mb-2 text-12 text-muted">Pager</p>
            <div className="w-140">
              <ZenithPager from={1} to={12} total={489} onNext={() => undefined} />
              <ZenithPager from={1} to={3} total={3} />
            </div>
          </div>

          <div>
            <p className="mb-2 text-12 text-muted">ListRow</p>
            <div className="w-100 overflow-hidden rounded-2xl bg-card">
              <ListRow
                title="Carlos M."
                sub="Pix pago · 14:32"
                trailing={<span className="data-value text-sm">R$ 129,90</span>}
                badge={<TxStatusBadge status="paid" />}
                onClick={() => undefined}
              />
              <ListRow
                title="Juliana S."
                sub="Pix aguardando · 14:19"
                badge={<TxStatusBadge status="pending" />}
              />
            </div>
          </div>

          <div className="w-100">
            <p className="mb-2 text-12 text-muted">KeyValue</p>
            <KeyValue rows={ZENITH_KV_ROWS} />
          </div>

          <div className="w-130">
            <p className="mb-2 text-12 text-muted">VolumeCard</p>
            <VolumeCard data={ZENITH_VOLUME} />
          </div>

          <div className="w-105">
            <p className="mb-2 text-12 text-muted">IconListCard</p>
            <IconListCard
              title="Pendências"
              description="Precisam da sua atenção"
              items={ZENITH_ICON_ITEMS}
            />
          </div>

          <div className="w-100">
            <p className="mb-2 text-12 text-muted">Timeline</p>
            <ZenithTimeline items={ZENITH_TIMELINE} />
          </div>

          <div className="w-120">
            <p className="mb-2 text-12 text-muted">CodeBlock</p>
            <CodeBlock
              label="payload"
              body={JSON.stringify({ event: 'transaction.paid', id: 'txn_01J9K2' }, null, 2)}
            />
          </div>

          <div className="w-100">
            <p className="mb-2 text-12 text-muted">ToggleRow</p>
            <ToggleRow
              icon={AlertTriangle}
              title="Saque automático"
              sub="Transfere para a chave Pix ao fechar o dia"
              checked={zenithToggle}
              onChange={setZenithToggle}
            />
            <ToggleRow title="Modo test" checked={false} onChange={() => undefined} last />
          </div>

          <div>
            <p className="mb-2 text-12 text-muted">RouteDrawer</p>
            <Btn
              kind="card"
              onClick={() => {
                setZenithDrawerOpen(true);
              }}
            >
              Abrir route-drawer
            </Btn>
            <RouteDrawer
              open={zenithDrawerOpen}
              onClose={() => {
                setZenithDrawerOpen(false);
              }}
              title="Pix recebido"
              sub="txn_01J9K2QH7V · 14:32"
            >
              <KeyValue rows={ZENITH_KV_ROWS} />
            </RouteDrawer>
          </div>
        </div>
      </Section>
    </div>
  );
}

export const Route = createFileRoute('/dev/kit')({
  beforeLoad: () => {
    if (!import.meta.env.DEV) {
      throw notFound();
    }
  },
  component: KitGallery,
});
